import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "@/config";
import { toast } from "sonner";
import { ChevronRight, List, Plus, Trash2, Edit2, X, Check, Users } from "lucide-react";
import { API } from "@/config";
import { usePageTitle } from "@/hooks/usePageTitle";

const MailingListsPage = ({ isAdmin, adminPassword }) => {
  usePageTitle('Mailing Lists');
  const navigate = useNavigate();
  

  const [lists, setLists] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', slug: '' });
  const [submitting, setSubmitting] = useState(false);

  // Memoized password getter — clean and safe
  const getAdminPassword = useMemo(() => () => {
    if (adminPassword) return adminPassword; // Prefer prop
    return localStorage.getItem('spoleto_admin_auth') || localStorage.getItem('adminAuth') || '';
  }, [adminPassword]);

  // Authentication check on mount
  useEffect(() => {
    fetchData();
  }, [isAdmin, navigate]);

  // Fetch data with AbortController for cleanup
  const fetchData = async () => {
    const controller = new AbortController();
    setLoading(true);

    try {
      const password = getAdminPassword();
      const headers = password ? { Authorization: `Bearer ${password}` } : {};

      const [listsRes, subscribersRes] = await Promise.all([
        api.get(`/newsletter/lists`, { signal: controller.signal, headers }),
        api.get(`/newsletter/subscribers`, { signal: controller.signal, headers })
      ]);

      setLists(listsRes.data);
      setSubscribers(subscribersRes.data);
    } catch (err) {
      if (axios.isCancel(err)) return; // Request canceled — component unmounted
      console.error('Failed to fetch data:', err);

      // Fallback: try to at least get lists
      try {
        const password = getAdminPassword();
        const headers = password ? { Authorization: `Bearer ${password}` } : {};
        const listsRes = await api.get(`/newsletter/lists`, { signal: controller.signal });
        setLists(listsRes.data);
      } catch (e) {
        toast.error('Failed to fetch mailing lists');
      }
    } finally {
      setLoading(false);
    }

    return () => controller.abort(); // Cleanup on unmount — but this is inside useEffect, so placed here for clarity
  };

  // Count confirmed subscribers per list
  const getSubscriberCount = (listSlug) => {
    return subscribers.filter(sub =>
      sub.status === 'confirmed' && Array.isArray(sub.lists) && sub.lists.includes(listSlug)
    ).length;
  };

  // Generate slug from name, ensure uniqueness
  const generateUniqueSlug = (name, existingSlugs = lists.map(l => l.slug)) => {
    let baseSlug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');

    if (!baseSlug) return 'list';

    let slug = baseSlug;
    let counter = 1;

    while (existingSlugs.includes(slug)) {
      slug = `${baseSlug}_${counter}`;
      counter++;
    }

    return slug;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.slug) {
      toast.error('Name and slug are required');
      return;
    }

    setSubmitting(true);
    try {
      const password = getAdminPassword();
      const headers = { Authorization: `Bearer ${password}` };

      if (editingList) {
        await api.put(
          `/newsletter/lists/${editingList.id}`,
          formData,
          { headers }
        );
        toast.success('List updated!');
      } else {
        await api.post(
          `/newsletter/lists`,
          formData,
          { headers }
        );
        toast.success('List created!');
      }

      setShowForm(false);
      setEditingList(null);
      setFormData({ name: '', description: '', slug: '' });
      fetchData(); // Refresh data
    } catch (err) {
      const errorDetail =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.statusText ||
        'Failed to save list. Please try again.';
      toast.error(errorDetail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (list) => {
    setEditingList(list);
    setFormData({
      name: list.name,
      description: list.description || '',
      slug: list.slug,
    });
    setShowForm(true);
  };

  const handleDelete = async (list) => {
    if (!window.confirm(`Delete "${list.name}" list? Subscribers will remain in the system but won't be associated with this list.`)) return;

    try {
      const password = getAdminPassword();
      const headers = { Authorization: `Bearer ${password}` };
      await api.delete(`/newsletter/lists/${list.id}`);
      toast.success('List deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete list');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingList(null);
    setFormData({ name: '', description: '', slug: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4 flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="mailing-lists-page">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/spoleto/newsletter')}
          className="text-[#0000FF] flex items-center gap-2 mb-8 hover:gap-3 transition-all text-sm"
        >
          <ChevronRight size={16} className="rotate-180" />
          Back to Subscribers
        </button>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <List size={32} className="text-[#0000FF]" />
            <h1 className="text-3xl md:text-4xl font-bold">Mailing Lists</h1>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-list-btn"
          >
            <Plus size={16} />
            Add List
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-bold mb-4">{editingList ? 'Edit List' : 'Create New List'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({
                      ...formData,
                      name,
                      slug: generateUniqueSlug(name, lists.map(l => l.slug)),
                    });
                  }}
                  placeholder="e.g., Weekly Digest"
                  className="w-full bg-gray-50 border border-gray-200 px-4 py-3 focus:border-[#0000FF] outline-none"
                  data-testid="list-name-input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Slug (unique identifier) *</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => {
                    const input = e.target.value;
                    // Allow manual override but validate on blur or submit
                    setFormData({ ...formData, slug: input });
                  }}
                  placeholder="e.g., weekly_digest"
                  className="w-full bg-gray-50 border border-gray-200 px-4 py-3 focus:border-[#0000FF] outline-none font-mono text-sm"
                  data-testid="list-slug-input"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Auto-generated from name. Edit if needed.
                </p>
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description shown to subscribers"
                  className="w-full bg-gray-50 border border-gray-200 px-4 py-3 focus:border-[#0000FF] outline-none"
                  data-testid="list-description-input"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      {editingList ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary flex items-center gap-2"
                >
                  <X size={16} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lists Table */}
        <div className="bg-white border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left p-4 text-sm text-muted-foreground font-medium">Name</th>
                <th className="text-left p-4 text-sm text-muted-foreground font-medium">Slug</th>
                <th className="text-center p-4 text-sm text-muted-foreground font-medium">Subscribers</th>
                <th className="text-left p-4 text-sm text-muted-foreground font-medium hidden md:table-cell">Description</th>
                <th className="text-right p-4 text-sm text-muted-foreground font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lists.map(list => {
                const count = getSubscriberCount(list.slug);
                return (
                  <tr key={list.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-4 font-medium">{list.name}</td>
                    <td className="p-4 font-mono text-sm text-muted-foreground">{list.slug}</td>
                    <td className="p-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm ${
                        count > 0 ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-muted-foreground'
                      }`}>
                        <Users size={14} />
                        {count}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground hidden md:table-cell">{list.description}</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(list)}
                          className="p-2 hover:bg-gray-100 text-muted-foreground hover:text-white transition-colors"
                          aria-label={`Edit list: ${list.name}`}
                          data-testid={`edit-list-${list.slug}`}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(list)}
                          className="p-2 hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
                          aria-label={`Delete list: ${list.name}`}
                          data-testid={`delete-list-${list.slug}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {lists.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-muted-foreground">
                    No mailing lists yet. Click "Add List" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MailingListsPage;
